import { createAction, type GetThunkAPI } from '@reduxjs/toolkit';
//Redux
import { JSONParser } from '@streamparser/json-whatwg';

//Types
import { IntentWorkflowActions } from './workflowsv2Types';
import type {
  ContextVariables,
  CsatData,
  IntentConversationResponse,
  InitIntentInterface,
  ConversationNotFoundError,
  ResetConversationInterface,
  CsatTranslatedText,
  AttemptedToFetchRedactedConversationError,
  WorkflowConfigArgs,
  CsatFeedbackRequestFields,
  QuickFeedbackRequestFields,
  SendFeedbackRequest,
  StreamingResponse,
  WidgetComponent,
  BadRequestError,
  TooManyRequestsError,
  PersistedWidgetComponent,
  SendConversationAlertRequest,
  ComponentTypes,
  KnowledgeResult,
  KnowledgeSearchResponse,
  WidgetArticleSearchOrigin,
  ClearConversationReason,
} from 'api/types';
import {
  isConversationNotFoundError,
  isAttemptedToFetchRedactedConversationError,
  isCsatFeedbackRequestFields,
  isBadRequestError,
  isTooManyRequestsError,
  isWorkflowTagSelectorComponent,
  isSelectIntentComponent,
} from 'api/types';

import {
  resetReduxState,
  configWidget,
  reloadTranslatedWidgetConfig,
  resetPreviewWidgetConfig,
  sendTrackingEvent,
  setCsatType,
  setCsatVisibility,
  setDidSubmit,
  setHandoffStatus,
  setIntentTitle,
  setStepContextVariables,
  setStepSent,
  setUserInteractions,
  setLastProactiveTriggerId,
} from 'actions/workflows/workflows';
import {
  handleInitIntentWorkflowTrackingEvents,
  handleIntentWorkflowTrackingEvents,
  sendResizeiframeMessage,
} from 'components/App/utils';
import ApiService from '../../services/ApiService';
import {
  addScreenReaderNotification,
  receiveStreamResponse,
  selectCsatSurveyVisibilityOnClose,
  selectFeatureFlags,
  selectFreeFormQuery,
  selectInitialIntentId,
  selectIntentIdToLoad,
  selectIsBuilderPreview,
  selectIsCsatPromptableForMinimize,
  selectIsCsatTextEmptyFromResponse,
  selectIsFloatingBarFocused,
  selectIsFreeFormInputDisabled,
  selectIsFreeFormInputVisible,
  selectIsLiveChatMode,
  selectIsMobileSDK,
  selectIsSolveConfigPreview,
  selectLastProactiveTriggerId,
  selectLastWidgetComponent,
  selectSelectedIntentIndex,
  selectWidgetComponents,
  selectWidgetMode,
  selectWindowUpdatedContextVariables,
  selectWorkflowsConfig,
  selectWorkflowTags,
  selectWorkflowVersion,
  setCsatStatesByKey,
  setIsFloatingBarFocused,
  setStreaming,
} from 'reducers/workflows';
import { UAParser } from 'ua-parser-js';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import getParentOrigin from 'utils/getParentOrigin';
import type { CustomFetchErrorObject } from 'api/customFetch';
import { CustomFetchError, rejectWithCustomFetchError } from 'api/customFetch';
import {
  selectIsInteractiveEmail,
  selectIsPrivacyConsented,
  selectLaunchStyle,
  selectOnLoadConfig,
  selectPlacement,
  selectRawParams,
  selectSize,
  selectSolveWidgetProduct,
} from 'reducers/config';
import {
  postForethoughtWidgetPreviewErrorMessage,
  postForethoughtWidgetPreviewLogMessage,
} from './utils';
import { V2_WORKFLOWS_URL } from 'api/requestsV2';
import { baseURL } from 'api/forethoughtFetch';
import mapValues from 'just-map-values';
import { LOW_RATING_CAP } from 'components/Csat/constants';
import {
  selectIsLiveChatInputDisabled,
  selectIsLiveChatInputVisible,
  setLiveChatQuery,
  setLiveChatStatus,
  setSubmittedCsatResult,
} from 'reducers/live-chat';
import { setSentryContext } from 'api/utils';
import { captureException } from '@sentry/react';
import type { AppDispatch } from 'store';
import { createAppAsyncThunk } from 'createAppAsyncThunk';
import { applyZopimFunction } from 'embed/zopim';
import { addRawParams } from 'actions/config';
import { postMessage } from 'utils/postMessage';
import {
  selectIsViewingImage,
  selectIsWidgetVisible,
  selectWidgetStyle,
} from 'reducers/main';
import {
  clearImageViewer,
  closeWidget,
  navigateBack,
  openWidget,
  setSelectedTab,
} from 'actions/main';
import type { RootState } from 'reducers/root';
import type { ForethoughtWidgetLoadedEvent } from 'embed/embed';
import type { AppThunk } from 'appThunk';
import {
  clearLiveChat,
  endLiveChat,
  handleLiveChatInput,
} from 'actions/live-chat';

export const setFreeFormQuery = createAction<string>(
  IntentWorkflowActions.SET_FREE_FORM_QUERY,
);

export const setConversationId = createAction<string>(
  IntentWorkflowActions.SET_CONVERSATION_ID,
);

/**
 * Updates the conversation.
 *
 * @param {Object} params - The parameters for updating the conversation.
 * @param {ContextVariables} params.contextVariables - The context variables to update.
 * @param {boolean} [params.isContinueAutoflowIntent=false] - If true, will use the 'continue-autoflow-intent' endpoint.
 */
export const updateIntentConversation = createAppAsyncThunk<
  IntentConversationResponse,
  { contextVariables: ContextVariables; isContinueAutoflowIntent?: boolean },
  {
    rejectValue:
      | ConversationNotFoundError
      | BadRequestError
      | TooManyRequestsError
      | (BadRequestError & { isInteractiveEmail: boolean })
      | (TooManyRequestsError & { isInteractiveEmail: boolean });
  }
>(
  IntentWorkflowActions.UPDATE_INTENT_CONVERSATION,
  async (
    { contextVariables, isContinueAutoflowIntent = false },
    { dispatch, getState, rejectWithValue },
  ) => {
    const state = getState();
    const { aiInstructionsForPreview, intentFiltersForPreview } = state.config;
    const {
      conversationId,
      isSolveConfigPreview: isPreview,
      nextStepId,
      transcriptLength,
      widgetComponents,
    } = state.workflows;
    const isInteractiveEmail = selectIsInteractiveEmail(state);
    const initialIntentId = selectInitialIntentId(state);
    const windowUpdatedContextVariables =
      selectWindowUpdatedContextVariables(state);
    const updatedContext = {
      ...windowUpdatedContextVariables,
      /**
       * These take priority because they are the output of the
       * previous step and include user inputs.
       */
      ...contextVariables,
    };

    try {
      const data = await (isContinueAutoflowIntent
        ? ApiService.request('continue-autoflow-intent', {
            urlValues: { conversationId },
          })
        : ApiService.request('update-intent-conversation', {
            data: {
              ai_instruction_filters_for_preview: aiInstructionsForPreview,
              context_variables: updatedContext,
              intent_filters_for_preview: intentFiltersForPreview,
              next_step_id: nextStepId,
              transcript_length: transcriptLength,
            },
            urlValues: { conversationId },
          }));

      if (isPreview) {
        postForethoughtWidgetPreviewLogMessage(
          data.conversation_id,
          data.preview_logs,
        );
      }

      handleIntentWorkflowTrackingEvents(
        updatedContext,
        widgetComponents,
        dispatch,
        {
          intent_id: data.intent_id,
          workflow_id: data.workflow_id,
        },
      );

      handleStream(data.widget_components, dispatch);

      return data;
    } catch (error) {
      if (isPreview) {
        postForethoughtWidgetPreviewErrorMessage(error);
      }
      if (error instanceof CustomFetchError) {
        const errorData = error.data;

        if (isConversationNotFoundError(errorData)) {
          return rejectWithValue(errorData);
        }

        if (
          (isBadRequestError(errorData) || isTooManyRequestsError(errorData)) &&
          isInteractiveEmail
        ) {
          dispatch(
            initIntentConversation({ intent: { intent_id: initialIntentId } }),
          );
          return rejectWithValue({ ...errorData, isInteractiveEmail: true });
        }
      }

      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();

      // Abort this request if current browser tab is not the primary tab to
      // prevent "Duplicate update conversation request" error:
      if (!selectIsCurrentTabPrimary(state)) {
        return false;
      }
    },
  },
);

export const sendPartialCsatFeedback = createAppAsyncThunk<
  IntentConversationResponse,
  void,
  { rejectValue: ConversationNotFoundError }
>(
  IntentWorkflowActions.SEND_CONVERSATION_FEEDBACK,
  async (_: void, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const { conversationId, isSolveConfigPreview: isPreview } = state.workflows;
    const {
      additionalFeedback,
      highRatingReasonIndices,
      isResolved,
      lowRatingReasonIndices,
      rating,
    } = state.workflows.csatStates;

    let csatFeedback: string[] = [];
    if (state.workflows.workflowsConfig?.csat_config) {
      const {
        reasons_for_high_ratings: reasonsForHighRatings,
        reasons_for_low_ratings: reasonsForLowRatings,
      } = state.workflows.workflowsConfig.csat_config;
      if (rating <= LOW_RATING_CAP) {
        csatFeedback = lowRatingReasonIndices.map(
          index => reasonsForLowRatings[index] || '',
        );
      } else {
        csatFeedback = highRatingReasonIndices.map(
          index => reasonsForHighRatings[index] || '',
        );
      }
    }

    const payload: SendFeedbackRequest = {
      csat_feedback_fields: {
        additional_feedback: additionalFeedback,
        csat_feedback: csatFeedback,
        csat_resolve: isResolved,
        csat_score: rating,
        csat_trigger_point_prompted: false,
        is_partial_submission: true,
        resume_from_trigger_point: false,
        routing_intent_workflow_id: undefined,
      },
    };

    try {
      const data = await ApiService.request('send-conversation-feedback', {
        data: payload,
        urlValues: { conversationId },
      });

      if (isPreview) {
        postForethoughtWidgetPreviewLogMessage(
          data.conversation_id,
          data.preview_logs,
        );
      }

      handleStream(data.widget_components, dispatch);

      return data;
    } catch (error) {
      if (isPreview) {
        postForethoughtWidgetPreviewErrorMessage(error);
      }
      if (error instanceof CustomFetchError) {
        const errorData = error.data;

        if (isConversationNotFoundError(errorData)) {
          return rejectWithValue(errorData);
        }
      }

      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();

      // Abort this request if current browser tab is not the primary tab to
      // prevent "Duplicate update conversation request" error:
      if (!selectIsCurrentTabPrimary(state)) {
        return false;
      }
    },
  },
);

export const sendConversationFeedback = createAppAsyncThunk<
  IntentConversationResponse,
  {
    feedbackFields: CsatFeedbackRequestFields | QuickFeedbackRequestFields;
  },
  { rejectValue: ConversationNotFoundError }
>(
  IntentWorkflowActions.SEND_CONVERSATION_FEEDBACK,
  async (parameters, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const { conversationId, isSolveConfigPreview: isPreview } = state.workflows;
    const feedbackFields = parameters.feedbackFields;
    const isCsatFeedback = isCsatFeedbackRequestFields(feedbackFields);
    const additionalFeedback =
      (feedbackFields as CsatFeedbackRequestFields).additional_feedback ||
      (feedbackFields as QuickFeedbackRequestFields).additionalFeedback;
    const payload: SendFeedbackRequest = isCsatFeedback
      ? {
          csat_feedback_fields: {
            additional_feedback: additionalFeedback,
            csat_feedback: feedbackFields.csat_feedback,
            csat_resolve: feedbackFields.csat_resolve,
            csat_score: feedbackFields.csat_score,
            csat_trigger_point_prompted:
              feedbackFields.csat_trigger_point_prompted,
            is_partial_submission: feedbackFields.is_partial_submission,
            resume_from_trigger_point: feedbackFields.resume_from_trigger_point,
            routing_intent_workflow_id:
              feedbackFields.routing_intent_workflow_id,
          },
        }
      : {
          feedback_fields: {
            additional_feedback: feedbackFields.additionalFeedback,

            article_step_data: feedbackFields.articleStepData,

            is_useful: feedbackFields.isUseful,

            knowledge_articles: feedbackFields.knowledgeArticles.map(
              knowledgeArticle => ({
                ...knowledgeArticle,
                full_html: undefined,
              }),
            ),

            text_message_override: feedbackFields.textMessageOverride,
          },
        };

    try {
      const data = await ApiService.request('send-conversation-feedback', {
        data: payload,
        urlValues: { conversationId },
      });

      if (isPreview) {
        postForethoughtWidgetPreviewLogMessage(
          data.conversation_id,
          data.preview_logs,
        );
      }

      handleStream(data.widget_components, dispatch);

      return data;
    } catch (error) {
      if (isPreview) {
        postForethoughtWidgetPreviewErrorMessage(error);
      }
      if (error instanceof CustomFetchError) {
        const errorData = error.data;

        if (isConversationNotFoundError(errorData)) {
          return rejectWithValue(errorData);
        }
      }

      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();

      // Abort this request if current browser tab is not the primary tab to
      // prevent "Duplicate update conversation request" error:
      if (!selectIsCurrentTabPrimary(state)) {
        return false;
      }
    },
  },
);

export const resetIntentConversation = createAppAsyncThunk<
  ResetConversationInterface,
  void,
  { rejectValue: ConversationNotFoundError }
>(
  IntentWorkflowActions.RESET_INTENT_CONVERSATION,
  async (_: void, { getState, rejectWithValue }) => {
    const state = getState();
    const { conversationId, nextStepId, transcriptLength } = state.workflows;
    const isPreview = state.workflows.isSolveConfigPreview;
    const windowUpdatedContextVariables =
      selectWindowUpdatedContextVariables(state);

    try {
      const data = await ApiService.request('restart-intent-conversation', {
        data: {
          context_variables: windowUpdatedContextVariables,
          next_step_id: nextStepId,
          transcript_length: transcriptLength,
        },
        urlValues: { conversationId },
      });

      return data;
    } catch (error) {
      if (isPreview) {
        postForethoughtWidgetPreviewErrorMessage(error);
      }
      if (error instanceof CustomFetchError) {
        const errorData = error.data;

        if (isConversationNotFoundError(errorData)) {
          return rejectWithValue(errorData);
        }
      }

      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const initIntentConversation = createAppAsyncThunk<
  IntentConversationResponse,
  {
    from_initial_intent_id?: boolean;
    intent: InitIntentInterface;
    isProactive?: boolean;
  },
  { rejectValue: ConversationNotFoundError }
>(
  IntentWorkflowActions.INIT_INTENT_CONVERSATION,
  async (
    { from_initial_intent_id, intent, isProactive = false },
    { dispatch, getState, rejectWithValue },
  ) => {
    const state = getState();
    const {
      conversationId,
      customParams,
      nextStepId,
      parentUrl,
      previousIntentIds,
      transcriptLength,
    } = state.workflows;
    const { aiInstructionsForPreview, intentFiltersForPreview } = state.config;
    const isPreview = state.workflows.isSolveConfigPreview;
    const solveWidgetProduct = selectSolveWidgetProduct(state);
    const isMobileSDK = selectIsMobileSDK(state);
    const windowUpdatedContextVariables =
      selectWindowUpdatedContextVariables(state);
    const proactiveTriggerId = selectLastProactiveTriggerId(state);
    const contextVariables = {
      ...customParams,
      /**
       * These take priority because the customer may have
       * code / events that update context after the page load
       * before we initialize the conversation.
       */
      ...windowUpdatedContextVariables,
    };

    // Origin information
    const uap = new UAParser();
    const { browser, device, os } = uap.getResult();
    try {
      const data = await ApiService.request('init-intent-conversation', {
        data: {
          ai_instruction_filters_for_preview: aiInstructionsForPreview,
          context_variables: contextVariables,
          from_initial_intent_id: from_initial_intent_id
            ? from_initial_intent_id
            : null,
          ...intent,
          conversation_id: conversationId,
          intent_filters_for_preview: intentFiltersForPreview,
          next_step_id: nextStepId,
          origin_information: {
            browser_name: browser.name ?? '',
            /**
             * Device type can be empty string if no device is detected on UserAgent,
             * There is no good way to identify desktop devices through the parser package
             * https://github.com/faisalman/ua-parser-js/issues/182
             */
            device_type: device.type || 'desktop',
            // mobile_app_id,
            origin: getParentOrigin(),
            os: os.name ?? '',
            parent_url: parentUrl,
            solve_ui_type: isMobileSDK ? 'sdk_webview' : 'web',

            user_agent: window.navigator.userAgent,
          },
          previous_intent_ids: previousIntentIds,
          solve_widget_product: solveWidgetProduct,
          transcript_length: transcriptLength,
          widget_parameters: state.config.rawParams,
        },
      });

      if (isPreview) {
        postForethoughtWidgetPreviewLogMessage(
          data.conversation_id,
          data.preview_logs,
        );
      }

      handleInitIntentWorkflowTrackingEvents({
        additionalParams: {
          intent_id: data.intent_id,
          workflow_id: data.workflow_id,
        },
        dispatch,
        intent,
        isProactive,
        proactiveTriggerId,
      });

      handleStream(data.widget_components, dispatch);

      dispatch(setLastProactiveTriggerId(''));

      return data;
    } catch (error) {
      if (isPreview) {
        postForethoughtWidgetPreviewErrorMessage(error);
      }
      if (error instanceof CustomFetchError) {
        const errorData = error.data;

        if (isConversationNotFoundError(errorData)) {
          return rejectWithValue(errorData);
        }
      }

      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();

      // Abort this request if current browser tab is not the primary tab to
      // prevent "Duplicate initialize conversation request" error:
      if (!selectIsCurrentTabPrimary(state)) {
        return false;
      }

      // Abort this request if there's a pending initConversation request
      if (
        state.workflows.conversationStatus === 'start' &&
        state.workflows.loading
      ) {
        return false;
      }
    },
  },
);

export const sendFreeFormQuery = createAppAsyncThunk<
  IntentConversationResponse,
  string,
  {
    rejectValue:
      | ConversationNotFoundError
      | BadRequestError
      | TooManyRequestsError
      | (BadRequestError & { isInteractiveEmail: boolean })
      | (TooManyRequestsError & { isInteractiveEmail: boolean });
  }
>(
  IntentWorkflowActions.SEND_FREE_FORM_QUERY,
  async (query: string, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const { conversationId, nextStepId, transcriptLength } = state.workflows;
    const { aiInstructionsForPreview, intentFiltersForPreview } = state.config;
    const isPreview = state.workflows.isSolveConfigPreview;
    const rawParams = selectRawParams(state);

    const rawParamsIntentId = String(rawParams?.['data-intent-id']);
    const isInteractiveEmail = selectIsInteractiveEmail(state);
    const initialIntentId = selectInitialIntentId(state);
    const windowUpdatedContextVariables =
      selectWindowUpdatedContextVariables(state);

    try {
      // All other free-form query cases:
      const data = await ApiService.request('send-free-form-query', {
        data: {
          ai_instruction_filters_for_preview: aiInstructionsForPreview,
          context_variables: windowUpdatedContextVariables,
          intent_filters_for_preview: intentFiltersForPreview,
          next_step_id: nextStepId,
          query,
          template_intent_id: rawParamsIntentId.startsWith('template')
            ? rawParamsIntentId
            : undefined,
          transcript_length: transcriptLength,
        },
        urlValues: { conversationId },
      });

      if (isPreview) {
        postForethoughtWidgetPreviewLogMessage(
          data.conversation_id,
          data.preview_logs,
        );
      }

      handleStream(data.widget_components, dispatch);

      return data;
    } catch (error) {
      if (isPreview) {
        postForethoughtWidgetPreviewErrorMessage(error);
      }
      if (error instanceof CustomFetchError) {
        const errorData = error.data;

        if (isConversationNotFoundError(errorData)) {
          return rejectWithValue(errorData);
        }

        if (
          (isBadRequestError(errorData) || isTooManyRequestsError(errorData)) &&
          isInteractiveEmail
        ) {
          dispatch(
            initIntentConversation({
              intent: { intent_id: initialIntentId },
            }),
          );
          return rejectWithValue({ ...errorData, isInteractiveEmail: true });
        }
      }

      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();

      // Abort this request if current browser tab is not the primary tab to
      // prevent duplicate calls:
      if (!selectIsCurrentTabPrimary(state)) {
        return false;
      }
    },
  },
);

export const submitCsatV2 = createAppAsyncThunk(
  IntentWorkflowActions.SUBMIT_CSAT,
  async (_: void, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const conversationId = state.workflows.conversationId || '';
    const triggeredFrom = state.workflows.csatStates.triggeredFrom;
    const {
      csatFeedback: csat_feedback,
      csatResolve: csat_resolve,
      csatScore: csat_score,
      isCsatDataSubmitted: submitted,
    } = state.workflows.csatFormData;
    const { csatType, didSubmit } = state.workflows.csatConfig;
    const csatData: CsatData = {
      csat_feedback,
      csat_resolve,
      csat_score,
      submitted,
    };

    try {
      const response = await ApiService.request('submit-intent-csat', {
        data: csatData,
        urlValues: { conversationId },
      });

      // Only send tracking event on first submission
      if (response.success && submitted && !didSubmit) {
        const trackingData = {
          ...csatData,
          csat_type: csatType,
        };
        dispatch(
          sendTrackingEvent({
            ...trackingData,
            event_type: 'widget-csat-submitted',
            triggered_from: triggeredFrom,
          }),
        );
        dispatch(setDidSubmit(true));
      }
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const submitCsatV3 = createAppAsyncThunk(
  IntentWorkflowActions.SUBMIT_CSAT_V3,
  async (
    { sendFeedbackToHelpdesk = false }: { sendFeedbackToHelpdesk?: boolean },
    { dispatch, getState, rejectWithValue },
  ) => {
    const state = getState();
    const conversationId = state.workflows.conversationId || '';
    // check livechat integration and pass it to CSAT BE
    const liveChatIntegration = state.liveChat.integration;
    const helpdeskConversationId = state.liveChat.chatId;
    const submittedCsatResult = state.liveChat.submitted_csat_result;
    const triggeredFrom = state.workflows.csatStates.triggeredFrom;
    const {
      additionalFeedback,
      hasSubmitted,
      highRatingReasonIndices,
      isResolved,
      lowRatingReasonIndices,
      rating,
    } = state.workflows.csatStates;
    const widgetInitMethod = state.config.widgetInitMethod;

    let feedback: string[] = [];
    if (state.workflows.workflowsConfig?.csat_config) {
      const {
        reasons_for_high_ratings: reasonsForHighRatings,
        reasons_for_low_ratings: reasonsForLowRatings,
      } = state.workflows.workflowsConfig.csat_config;
      if (rating <= LOW_RATING_CAP) {
        feedback = lowRatingReasonIndices.map(
          index => reasonsForLowRatings[index] || '',
        );
      } else {
        feedback = highRatingReasonIndices.map(
          index => reasonsForHighRatings[index] || '',
        );
      }
    }

    const csatData: CsatData = {
      additional_feedback:
        additionalFeedback.trim() === '' ? undefined : additionalFeedback,
      csat_feedback: feedback,
      csat_resolve: isResolved,
      csat_score: rating,
      helpdesk_conversation_id: helpdeskConversationId,
      live_chat_integration: liveChatIntegration,
      submitted: hasSubmitted,
    };
    const sendCsatToHelpdesk =
      state.workflows.workflowsConfig?.csat_config?.show_when_agent_chat_ends;

    try {
      const response = await ApiService.request('submit-intent-csat', {
        data: csatData,
        urlValues: { conversationId },
      });

      if (response.success) {
        dispatch(
          sendTrackingEvent({
            ...csatData,
            event_type: 'widget-csat-submitted',
            triggered_from: triggeredFrom,
          }),
        );
        dispatch(setDidSubmit(true));
        dispatch(setUserInteractions(response.user_interactions));
      }
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }

    /**
     * relay the CSAT to Zendesk if we're in Zopim one chat
     *
     * https://api.zopim.com/web-sdk/#zchat-sendchatrating-rating-callback
     * https://api.zopim.com/web-sdk/#zchat-sendchatcomment-comment-callback
     */
    if (liveChatIntegration === 'zendesk' && sendCsatToHelpdesk) {
      // send rating when debounced
      if (submittedCsatResult.csat_score !== rating) {
        if (rating > 0 && rating <= LOW_RATING_CAP) {
          applyZopimFunction(widgetInitMethod, 'sendChatRating', 'bad');
        } else if (rating > LOW_RATING_CAP) {
          applyZopimFunction(widgetInitMethod, 'sendChatRating', 'good');
        }
        dispatch(setSubmittedCsatResult({ csat_score: rating }));
      }

      // only send comment when submit button is clicked, not when debounced
      if (
        sendFeedbackToHelpdesk &&
        additionalFeedback.trim() &&
        submittedCsatResult.additional_feedback !== additionalFeedback
      ) {
        applyZopimFunction(
          widgetInitMethod,
          'sendChatComment',
          additionalFeedback,
        );
        dispatch(
          setSubmittedCsatResult({ additional_feedback: additionalFeedback }),
        );
      }
    }
  },
);

export const getTranslatedCsatText = createAppAsyncThunk<
  CsatTranslatedText,
  void,
  { rejectValue: AttemptedToFetchRedactedConversationError }
>(
  IntentWorkflowActions.GET_TRANSLATED_INTENT_CSAT_TEXT,
  async (_: void, { getState, rejectWithValue }) => {
    const state = getState();
    const conversationId = state.workflows.conversationId || '';

    try {
      const data = await ApiService.request('get-translated-csat-text', {
        urlValues: { conversationId },
      });

      return data;
    } catch (error) {
      if (error instanceof CustomFetchError) {
        const errorData = error.data;

        if (isAttemptedToFetchRedactedConversationError(errorData)) {
          return rejectWithValue(errorData);
        }
      }

      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const startNewIntentSelection = createAppAsyncThunk<
  { restartData: ResetConversationInterface } & WorkflowConfigArgs & {
      neverMindSelection: string;
    },
  string,
  { rejectValue: AttemptedToFetchRedactedConversationError }
>(
  IntentWorkflowActions.START_NEW_INTENT_SELECTION,
  async (neverMindSelection, { getState, rejectWithValue }) => {
    const state = getState();
    const { conversationId, nextStepId, transcriptLength } = state.workflows;
    const { rawParams } = state.config;
    const intentId = rawParams?.['data-intent-id'] as string | null | undefined;

    try {
      const widgetConfigResponse = await ApiService.request('widget-config', {
        data: {
          conversation_id: state.workflows?.conversationId || undefined,
          raw_params: rawParams,
        },
      });

      const widgetConfigData = {
        ...widgetConfigResponse,
        intentId,
      };

      const restartData = await ApiService.request(
        'restart-intent-conversation',
        {
          data: {
            context_variables: {},
            keep_previous_intent_ids: true,
            next_step_id: nextStepId,
            transcript_length: transcriptLength,
          },
          urlValues: { conversationId },
        },
      );

      return { restartData, ...widgetConfigData, neverMindSelection };
    } catch (error) {
      if (error instanceof CustomFetchError) {
        const errorData = error.data;

        if (isAttemptedToFetchRedactedConversationError(errorData)) {
          return rejectWithValue(errorData);
        }
      }

      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const getIntentsBySelectedTag = createAppAsyncThunk<
  WorkflowConfigArgs,
  void,
  { rejectValue: AttemptedToFetchRedactedConversationError }
>(
  IntentWorkflowActions.SELECT_WORKFLOW_TAG,
  async (_: void, { getState, rejectWithValue }) => {
    const state = getState();
    const { rawParams } = state.config;

    try {
      return await ApiService.request('widget-config', {
        data: {
          conversation_id: state.workflows?.conversationId || undefined,
          raw_params: {
            ...rawParams,
          },
        },
      });
    } catch (error) {
      if (error instanceof CustomFetchError) {
        const errorData = error.data;

        if (isAttemptedToFetchRedactedConversationError(errorData)) {
          return rejectWithValue(errorData);
        }
      }

      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

function makeStreamGetter(
  endpoint: 'knowledge-retrieval-stream' | 'autoflow-stream',
) {
  return async (
    _: void,
    {
      dispatch,
      getState,
      rejectWithValue,
    }: GetThunkAPI<{
      dispatch: AppDispatch;
      rejectValue: CustomFetchErrorObject;
      state: RootState;
    }>,
  ) => {
    const state = getState();
    const { conversationId } = state.workflows;

    const url = `${baseURL}/${V2_WORKFLOWS_URL}/conversation/${conversationId}/${endpoint}`;

    let res: Response;

    /**
     * Needed to be able to call `.json()` when handling an error after the
     * original response is consumed by the stream reader (`.json()` can be
     * called only once).
     */
    let resClone: Response;

    try {
      res = await fetch(url, {
        headers: mapValues(
          { 'Content-Type': 'application/x-ndjson', ...ApiService.headers },
          String,
        ),
      });
      resClone = res.clone();
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue({
          internalTraceId: '',
          message: error.message,
          method: 'get',
          response_data: null,
          status: 0,
          url,
        });
      }
      return;
    }

    if (!res.ok) {
      let responseData;
      try {
        responseData = await res.json();
      } catch (error) {
        if (error instanceof Error) {
          setSentryContext({
            errorMessage: error.message,
            origin: ApiService.headers['solve-origin'],
            requestData: { conversationId: conversationId },
            responseData,
            tags: [
              ...ApiService.sentryTags,
              { key: 'responseStatus', value: res.status },
            ],
          });
          captureException(error);

          return rejectWithValue({
            internalTraceId: res.headers.get('x-internal-trace-id') || '',
            message: error.message,
            method: 'get',
            response_data: responseData,
            status: res.status,
            url,
          });
        }
        return;
      }

      const error = new Error(`/${endpoint} status code: ${res.status}`);
      setSentryContext({
        errorMessage: error.message,
        origin: ApiService.headers['solve-origin'],
        requestData: { conversationId: conversationId },
        responseData,
        tags: [
          ...ApiService.sentryTags,
          { key: 'responseStatus', value: res.status },
        ],
      });
      captureException(error);

      return rejectWithValue({
        internalTraceId: res.headers.get('x-internal-trace-id') || '',
        message: error.message,
        method: 'get',
        response_data: responseData,
        status: res.status,
        url,
      });
    }

    const parser = new JSONParser({ paths: ['$'], separator: '\n' });
    const reader = res.body?.pipeThrough(parser).getReader();

    if (reader) {
      let isFirstResponse = true;
      const allChunks: StreamingResponse[] = [];

      while (true) {
        let done = false;
        let value;
        dispatch(setStreaming(true));

        try {
          const result = await reader.read();
          done = result.done;
          value = result.value;
        } catch (error) {
          if (error instanceof Error) {
            const responseData = await resClone.json();

            setSentryContext({
              errorMessage: error.message,
              origin: ApiService.headers['solve-origin'],
              requestData: { conversationId: conversationId },
              responseData,
              tags: [
                ...ApiService.sentryTags,
                { key: 'responseStatus', value: res.status },
              ],
            });
            captureException(error);

            return rejectWithValue({
              internalTraceId: res.headers.get('x-internal-trace-id') || '',
              message: error.message,
              method: 'get',
              response_data: responseData,
              status: res.status,
              url,
            });
          }
          return;
        }

        if (done) {
          /**
           * we need the setTimeout so that this function can return, and redux can handle the
           * fulfilled of the knowledge retrieval stream action, after then we want to trigger the next
           * knowledge retrieval stream request.
           * Redux is synchronous, and this timeout would only be ran after all synchronous code are finished,
           * so a timeout of 0 is sufficient.
           */
          setTimeout(() => {
            const allWidgetComponents = allChunks.flatMap(streamResponse =>
              'widget_components' in streamResponse
                ? streamResponse.widget_components
                : [],
            );
            handleStream(allWidgetComponents, dispatch);
          }, 0);
          return;
        }

        allChunks.push(value?.value as unknown as StreamingResponse);
        dispatch(
          receiveStreamResponse({
            isFirstResponse,
            receivedAtInMilliseconds: Date.now(),
            response: value?.value as unknown as StreamingResponse,
          }),
        );

        isFirstResponse = false;
      }
    }
  };
}

export const getKnowledgeRetrievalStream = createAppAsyncThunk<
  void,
  void,
  { rejectValue: CustomFetchErrorObject }
>(
  IntentWorkflowActions.GET_KNOWLEDGE_RETRIEVAL_STREAM,
  makeStreamGetter('knowledge-retrieval-stream'),
  {
    condition: (_, { getState }) => {
      const state = getState();

      // Abort this request if current browser tab is not the primary tab to
      // prevent duplicate calls:
      if (!selectIsCurrentTabPrimary(state)) {
        return false;
      }
    },
  },
);

export const getAutoflowStream = createAppAsyncThunk<
  void,
  void,
  { rejectValue: CustomFetchErrorObject }
>(
  IntentWorkflowActions.GET_AUTOFLOW_STREAM,
  makeStreamGetter('autoflow-stream'),
  {
    condition: (_, { getState }) => {
      const state = getState();

      // Abort this request if current browser tab is not the primary tab to
      // prevent duplicate calls:
      if (!selectIsCurrentTabPrimary(state)) {
        return false;
      }
    },
  },
);

export const updateHelpdeskConversation = createAppAsyncThunk(
  IntentWorkflowActions.UPDATE_HELPDESK_CONVERSATION,
  async (
    {
      forethought_conversation_id,
      helpdesk_conversation_id,
      include_attachments,
      include_transcript,
      integration,
      step_type,
    }: {
      forethought_conversation_id: string | null;
      helpdesk_conversation_id: string;
      include_attachments: boolean | null;
      include_transcript: boolean | null;
      integration: string;
      step_type?: ComponentTypes;
    },
    { getState, rejectWithValue },
  ) => {
    const state = getState();

    try {
      return await ApiService.request('update-helpdesk-conversation', {
        data: {
          helpdesk_conversation_id,
          include_attachments,
          include_transcript,
          integration,
          step_type,
        },
        urlValues: {
          conversationId:
            forethought_conversation_id || state.workflows.conversationId,
        },
      });
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

function handleStream(
  widgetComponents: Array<WidgetComponent> | undefined,
  dispatch: AppDispatch,
) {
  if (
    widgetComponents?.some(
      widgetComponent =>
        widgetComponent.component_type === 'knowledge_retrieval_stream',
    )
  ) {
    dispatch(getKnowledgeRetrievalStream());
  } else if (
    widgetComponents?.some(
      widgetComponent => widgetComponent.component_type === 'autoflow_stream',
    )
  ) {
    dispatch(getAutoflowStream());
  }
}

export const getPersistedLiveChatComponents = createAppAsyncThunk<
  WidgetComponent[],
  void
>(
  IntentWorkflowActions.GET_PERSISTED_LIVE_CHAT_COMPONENTS,
  async (_: void, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const conversationId = state.workflows.conversationId || '';

    try {
      const widgetComponents = await ApiService.request(
        'get-widget-components',
        {
          urlValues: { conversationId },
        },
      );

      const widgetComponentsResponse = widgetComponents.widget_components.map(
        (widgetComponent: PersistedWidgetComponent) => {
          const component = {
            component_fields: widgetComponent.component_fields,
            component_type: widgetComponent.component_type,
            ...widgetComponent.component_attributes,
          } as WidgetComponent;

          return component;
        },
      );

      if (widgetComponentsResponse.length) {
        dispatch(setLiveChatStatus('active'));
      }

      return widgetComponentsResponse;
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const sendConversationAlert = createAppAsyncThunk(
  IntentWorkflowActions.SEND_CONVERSATION_ALERT,
  async (data: SendConversationAlertRequest, { getState, rejectWithValue }) => {
    const state = getState();
    const conversationId = state.workflows.conversationId || '';

    try {
      return await ApiService.request('send-conversation-alert', {
        data: data,
        urlValues: {
          conversationId: conversationId,
        },
      });
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const handleFreeFormInput = createAppAsyncThunk(
  IntentWorkflowActions.HANDLE_FREE_FORM_INPUT,
  async (_, { dispatch, getState }) => {
    const state = getState();

    // selectors
    const lastWidgetComponent = selectLastWidgetComponent(state);
    const widgetComponents = selectWidgetComponents(state);
    const workflowTags = selectWorkflowTags(state);
    const freeFormQuery = selectFreeFormQuery(state);
    const workflowConfig = selectWorkflowsConfig(state);
    const selectedIntentIndex = selectSelectedIntentIndex(state);

    // variables
    const isLastComponentWorkflowTagSelector =
      lastWidgetComponent &&
      isWorkflowTagSelectorComponent(lastWidgetComponent);
    const isLastComponentSelectIntentComponent =
      lastWidgetComponent && isSelectIntentComponent(lastWidgetComponent);
    const isQueryAWorkflowTag = workflowTags.includes(freeFormQuery.trim());

    dispatch(addScreenReaderNotification('Message sent'));

    function workflowTagSelectorSubmission() {
      const workflowTagSelectorIndex = widgetComponents.length - 1;

      if (isQueryAWorkflowTag) {
        dispatch(addRawParams({ 'data-ft-workflow-tag': freeFormQuery }));
        dispatch(getIntentsBySelectedTag());
      } else {
        dispatch(
          initIntentConversation({
            intent: { free_form_query: freeFormQuery },
          }),
        );
      }

      dispatch(
        setStepContextVariables({
          contextVariables: { tag_selector: freeFormQuery },
          index: workflowTagSelectorIndex,
        }),
      );
      dispatch(
        setStepSent({
          query: freeFormQuery,
          stepIndex: workflowTagSelectorIndex,
        }),
      );
      dispatch(setFreeFormQuery(''));
    }

    function handleSelectIntentSubmit() {
      if (!isLastComponentSelectIntentComponent) {
        return;
      }

      const intents = lastWidgetComponent.component_fields.show_top_n_intents
        ? workflowConfig?.top_n_intents
        : [];

      let selectedIntent: InitIntentInterface = {};
      if (selectedIntentIndex === intents?.length || freeFormQuery.trim()) {
        selectedIntent = { free_form_query: freeFormQuery };
        dispatch(
          setStepSent({
            query: selectedIntent.free_form_query,
          }),
        );
        dispatch(setFreeFormQuery(''));
      } else if (selectedIntentIndex !== null && intents) {
        const latestSelectedIntent = intents[selectedIntentIndex];
        if (latestSelectedIntent) {
          selectedIntent = {
            intent_id: latestSelectedIntent.intent_id,
          };
          dispatch(
            setStepSent({
              query:
                intents.find(
                  intent => intent.intent_id === latestSelectedIntent.intent_id,
                )?.intent_name || '',
            }),
          );
        }
      }

      dispatch(initIntentConversation({ intent: selectedIntent }));
    }

    // Free-form query tag selection for buttons step:
    if (isLastComponentWorkflowTagSelector) {
      workflowTagSelectorSubmission();
    } else if (isLastComponentSelectIntentComponent) {
      handleSelectIntentSubmit();
    } else {
      dispatch(sendFreeFormQuery(freeFormQuery));
    }
  },
);

export const modalRestartConversation = createAppAsyncThunk(
  IntentWorkflowActions.RESTART_CONVERSATION,
  async (_, { dispatch, getState }) => {
    const state = getState();

    // selectors
    const isBuilderPreview = selectIsBuilderPreview(state);
    const isSolveConfigPreview = selectIsSolveConfigPreview(state);
    const intentId = selectIntentIdToLoad(state);
    const workflowVersion = selectWorkflowVersion(state);
    const widgetMode = selectWidgetMode(state);
    const featureFlags = selectFeatureFlags(state);
    const isInteractiveEmail = selectIsInteractiveEmail(state);
    const initialIntentId = selectInitialIntentId(state);
    const isPrivacyConsented = selectIsPrivacyConsented(state);
    const workflowsConfig = selectWorkflowsConfig(state);

    const isAlwaysUseInitialIntentIdEnabled = featureFlags.includes(
      'always_use_initial_intent_id',
    );

    dispatch(setSelectedTab(0));

    dispatch(addScreenReaderNotification('Conversation restarted'));

    if ((isBuilderPreview || isSolveConfigPreview) && !!intentId) {
      dispatch(resetPreviewWidgetConfig(true));
      await dispatch(configWidget());

      dispatch(
        initIntentConversation({
          intent: {
            intent_id: intentId,
          },
        }),
      );
    } else if (isBuilderPreview && workflowVersion) {
      dispatch(resetPreviewWidgetConfig());
      await dispatch(configWidget());
    } else if (widgetMode === 'liveChat') {
      dispatch(
        clearConversation({
          reason: 'restart_in_livechat_mode',
        }),
      );
    } else if (workflowsConfig?.reset_widget_on_restart === true) {
      dispatch(
        clearConversation({
          reason: 'reset_widget_on_restart',
        }),
      );
    } else {
      postMessage({
        event: 'forethoughtWidgetPreviewRestartEvent',
      });
      await dispatch(resetIntentConversation());
      await dispatch(reloadTranslatedWidgetConfig());

      if (
        (isAlwaysUseInitialIntentIdEnabled || isInteractiveEmail) &&
        !!initialIntentId &&
        isPrivacyConsented
      ) {
        dispatch(
          initIntentConversation({
            intent: {
              intent_id: initialIntentId,
            },
          }),
        );
      }
    }

    if (history.length > 1) {
      dispatch(navigateBack());
    }
  },
);

export const modalMinimizeWidget = createAppAsyncThunk(
  IntentWorkflowActions.MINIMIZE_WIDGET,
  async (_, { dispatch, getState }) => {
    const state = getState();

    // selectors
    const isViewingImage = selectIsViewingImage(state);
    const isBuilderPreview = selectIsBuilderPreview(state);
    const isCsatTextEmptyFromResponse =
      selectIsCsatTextEmptyFromResponse(state);
    const showCsatSurvey = selectCsatSurveyVisibilityOnClose(state);
    const isCsatPromptable = selectIsCsatPromptableForMinimize(state);
    const isMobileSDK = selectIsMobileSDK(state);

    if (isViewingImage) {
      postMessage({
        event: 'forethoughtWidgetViewImage',
        open: false,
      });
      dispatch(clearImageViewer());
    } else if (isBuilderPreview) {
      dispatch(setIntentTitle(''));
      postMessage({ event: 'hideSolveWidget' });
      postMessage({ event: 'solveWidgetClosed' });
    } else {
      if (isCsatTextEmptyFromResponse) {
        dispatch(toggleModalVisibility(false));
      } else if (showCsatSurvey) {
        dispatch(setCsatType('close'));
        dispatch(setCsatVisibility(true));
      } else if (isCsatPromptable) {
        dispatch(
          setCsatStatesByKey({
            isCsatVisible: true,
            triggeredFrom: 'minimize',
          }),
        );
      } else if (isMobileSDK) {
        // Don't close on mobile. If we close it, the next time it opens it shows the button instead of the
        // widget, but do send the resize event so iOS closes the modal
        sendResizeiframeMessage(false);
      } else {
        dispatch(toggleModalVisibility(false));
      }
    }
  },
);

export const toggleModalVisibility = createAppAsyncThunk(
  IntentWorkflowActions.TOGGLE_MODAL_VISIBILITY,
  async (isVisible: boolean, { dispatch, getState }) => {
    const state = getState();

    // selectors
    const featureFlags = selectFeatureFlags(state);

    const isSolveUiMultipleTabsIframeSizeFixEnabled = featureFlags.includes(
      'solve_ui_multiple_tabs_iframe_size_fix',
    );

    if (isVisible) {
      dispatch(openWidget());
    } else {
      dispatch(closeWidget());
      if (!isSolveUiMultipleTabsIframeSizeFixEnabled) {
        sendResizeiframeMessage(false);
      }
    }
  },
);

export const handleClickOutsideIframe = createAppAsyncThunk(
  IntentWorkflowActions.HANDLE_CLICK_OUTSIDE_IFRAME,
  async (_, { dispatch, getState }) => {
    const state = getState();
    const widgetStyle = selectWidgetStyle(state);
    const isWidgetVisible = selectIsWidgetVisible(state);
    const isFloatingBarFocused = selectIsFloatingBarFocused(state);

    if (widgetStyle === 'floating_bar') {
      if (isFloatingBarFocused) {
        dispatch(setIsFloatingBarFocused(false));
      }

      if (isWidgetVisible) {
        dispatch(modalMinimizeWidget());
      }
    }
  },
);

export const searchKnowledge = createAppAsyncThunk(
  IntentWorkflowActions.SEARCH_KNOWLEDGE,
  async (
    { origin, query }: { origin: WidgetArticleSearchOrigin; query: string },
    { getState, rejectWithValue },
  ): Promise<KnowledgeSearchResponse | undefined> => {
    const state = getState();
    const conversationId = state.workflows.conversationId || '';
    const rawParams = selectRawParams(state);
    const isWidgetVisible = selectIsWidgetVisible(state);

    try {
      if (origin === 'floating_bar' && isWidgetVisible) {
        // do nothing because the widget is already open
        return;
      }

      const response = await ApiService.request('search-knowledge', {
        data: {
          query,
          widget_parameters: {
            ...rawParams,
          },
        },
        urlValues: {
          conversationId: conversationId,
        },
      });

      return response;
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const recordKnowledgeClick = createAppAsyncThunk(
  IntentWorkflowActions.SEARCH_KNOWLEDGE_CLICK,
  async (
    {
      document,
      fullResult,
    }: { document: KnowledgeResult; fullResult: KnowledgeSearchResponse },
    { getState, rejectWithValue },
  ) => {
    const state = getState();
    const conversationId = state.workflows.conversationId || '';

    try {
      ApiService.request('search-knowledge-click', {
        data: {
          document_id: document.document_id,
          document_link: document.link ?? '',
          document_title: document.title,
          query: fullResult.query,
        },
        urlValues: {
          conversationId: conversationId,
        },
      });
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const postForethoughtWidgetLoaded = (): AppThunk => (_, getState) => {
  const state = getState();
  const isWidgetVisible = selectIsWidgetVisible(state);
  const isLiveChatMode = selectIsLiveChatMode(state);
  const size = selectSize(state);
  const placement = selectPlacement(state);
  const launchStyle = selectLaunchStyle(state);
  const onLoadConfig = selectOnLoadConfig(state);

  const forethoughtWidgetLoadedData: ForethoughtWidgetLoadedEvent['data'] = {
    event: 'forethoughtWidgetLoaded',
    isLiveChatMode: isLiveChatMode,
    isOpen: isWidgetVisible,
    launchStyle,
    onLoadConfig,
    placement,
    size,
  };

  postMessage(forethoughtWidgetLoadedData);
  // also post it inside the iframe for the useHandleOnWidgetLoad hook
  window.postMessage(forethoughtWidgetLoadedData, '*');
};

export const clearConversation = createAppAsyncThunk<
  void,
  {
    additionalTrackingEventData?: Record<string, unknown>;
    initialQuery?: string;
    reason: ClearConversationReason;
    shouldEndLiveChat?: boolean;
  }
>(
  IntentWorkflowActions.CLEAR_CONVERSATION,
  async (
    {
      additionalTrackingEventData = {},
      initialQuery,
      reason,
      shouldEndLiveChat = true,
    },
    { dispatch, getState },
  ) => {
    const state = getState();
    const isCurrentTabPrimary = selectIsCurrentTabPrimary(state);

    /**
     * End live chat
     */
    if (isCurrentTabPrimary && shouldEndLiveChat) {
      /**
       * `shouldClearConversation` must be false in order to prevent infinite loop
       */
      await dispatch(
        endLiveChat({
          force: true,
          shouldClearConversation: false,
        }),
      );
    }

    /**
     * Clear live chat data in localStorage, SDK, etc...
     */
    await dispatch(clearLiveChat());

    /**
     * These will populate to other tabs via persistence.
     */
    if (isCurrentTabPrimary) {
      // send tracking event
      dispatch(
        sendTrackingEvent({
          ...additionalTrackingEventData,
          event_type: 'widget-conversation-cleared',
          reason,
        }),
      );

      // reset Redux state
      dispatch(resetReduxState());

      // call /widget-config to get a new conversation id
      await dispatch(configWidget({ initialQuery }));
    }
  },
);

export const successfulHandoffOnSubmit = createAppAsyncThunk<
  void,
  {
    handoffSuccessVariable?: string;
    updateConversation: (context: ContextVariables) => Promise<void>;
  }
>(
  IntentWorkflowActions.SUCCESSFUL_HANDOFF_ON_SUBMIT,
  async ({ handoffSuccessVariable = '', updateConversation }, { dispatch }) => {
    if (!handoffSuccessVariable) return;

    await updateConversation({ [handoffSuccessVariable]: true });
    dispatch(setHandoffStatus('not_started'));
    await dispatch(
      clearConversation({
        reason: 'successful_handoff_on_submit',
      }),
    );
  },
);

/**
 * Dispatched by window.Forethought('sendMessage'...) API
 */
export const handleSendMessage = createAppAsyncThunk(
  IntentWorkflowActions.HANDLE_SEND_MESSAGE,
  async ({ message }: { message: string }, { dispatch, getState }) => {
    const state = getState();
    const widgetMode = selectWidgetMode(state);

    if (!message) {
      console.error('Cannot send empty message');
      return;
    }

    switch (widgetMode) {
      case 'workflow':
        dispatch(setFreeFormQuery(message));

        if (!selectIsFreeFormInputVisible(state)) {
          console.error('Cannot send message -- input not visible');
          return;
        }

        // need to use the latest state
        if (selectIsFreeFormInputDisabled(getState())) {
          console.error('Cannot send message -- input currently disabled');
          return;
        }

        await dispatch(handleFreeFormInput());
        break;
      case 'liveChat':
        dispatch(setLiveChatQuery(message));

        if (!selectIsLiveChatInputVisible(state)) {
          console.error('Cannot send message -- input not visible');
          return;
        }

        // need to use the latest state
        if (selectIsLiveChatInputDisabled(getState())) {
          console.error('Cannot send message -- input currently disabled');
          return;
        }

        await dispatch(handleLiveChatInput());
        break;
    }
  },
);
